import React from 'react';
import { scroller } from 'react-scroll';
import loadable from '@loadable/component';

import ProductListContainer from '../product/components/product-container';
import LeftMenu from '../left-menu/left-menu-container';
import { withError } from '../shared/components/error-boundary';
import ConditionalWrapper from '../shared/components/conditional-wrapper';
import { Helpers } from '../core/src/helpers';
import CompareButton from '../compare/components/compare-button';
import Compare from '../compare/components/compare';
import FilterMenu from '../filters';
import FooterBanner from '../gift-guide/components/footer-banner';
import FilterCompareToggle from '../main/components/filter-compare-toggle';
import { urlParser, slpSearch } from './sale-page-filters';
import SaleProductList  from './components/sale-product-list';

const SearchProvider = loadable(() => import('../search-filter/components/search-container'));

const { memoize } = Helpers;

const EMPTY_OBJECT = {};
const EMPTY_ARRAY = [];

/**
 * Simple function that takes a search-filter query and counts number of filters
 * Since category or product group determin filter set ignore them as part of the filter count
 * @param {} query 
 */
const setFilterCount = (query) => {
    try{
        query = query || {};
        return Object.keys(query).filter(k => ['cat','pg'].indexOf(k) < 0).reduce((r, k) => {
            r += query[k].length;
            return r;
        },0);
    }catch(e){
        return 0;
    }
}

class SalePage extends React.PureComponent {
    static whyDidYouRender = true;
    static defaultProps = {
        activePG: { productsList: [] },
    };

    constructor(props) {
        super(props);
        //hacky temp solution fro preserving filter selection from tab to tab
        this.tabQuery = {};
        this.tabPriceRange = {};
    }

    getTabs = memoize(
        (disableSearch, pgKey, disableAutoFilters, isV2) => {
            let tabs = !isV2 ? [
                {
                    Component: Compare,
                    title: 'Compare',
                    active: disableSearch,
                    mobileTab: () => {
                        return <CompareButton />;
                    },
                },
            ] : [];
            if (!disableSearch) {
                tabs.unshift({
                    Component: FilterMenu,
                    title: 'Filter',
                    active: true,
                    tabProps: {
                        useAlias: true,
                        disableAutoFilters,
                        className: isV2 ? 'v2' : '',
                        isV2
                    },
                });
            }
            return tabs;
        },
        (disableSearch, pgKey, disableAutoFilters) => `${pgKey}${disableSearch}${disableAutoFilters}`
    );

    fetchFilters = (query, aggregations, sort, startPage, pageSize = 50, searchKey) => {
        let { pg, activePG, disableAutoHide, autoHideType } = this.props;
        //hacky way to perserve query from tab to tab
        this.tabQuery[searchKey] = Object.assign({}, query);

        return slpSearch(query, pg, activePG, disableAutoHide, autoHideType).then((resp) => {
            let { data, customAggregations } = resp;

            //TEMP FIX for price range, this should be fixed by making price a post filter
            if (this.tabPriceRange[searchKey]) {
                //if price range stored from first search, restore the price range from initial query
                let { max_price, min_price } = this.tabPriceRange[searchKey];
                data.aggregations.max_price = max_price;
                data.aggregations.min_price = min_price;
            } else {
                let { max_price, min_price } = data.aggregations;
                this.tabPriceRange[searchKey] = { max_price, min_price };
            }

            //reset scroll on search completion if scroll positon past top target + offset
            try {
                let slpHeader = scroller.get('SLPHeader');
                if (slpHeader) {
                    let { offsetTop } = slpHeader;
                    let scrollPos =
                        window.scrollY || window.scrollTop || document.getElementsByTagName('html')[0].scrollTop;
                    scrollPos > offsetTop + 300 && scroller.scrollTo('SLPHeader');
                }
            } catch (e) { }

            return { data, customAggregations };
        });
    };

    urlFormatter = (match, queryObj, customAggregations) => {
        const { activePG } = this.props;
        const { basename, dir, slug } = match.params;
        let [, , pg] = activePG.key.split(':');
        //assume filters should always be after slug or dir
        let url = `/${basename}/${dir}${slug ? '/' + slug : ''}`;
        //remove page element from serealization
        let urlQuery =
            queryObj && Object.keys(queryObj).length > 0 ? JSON.parse(JSON.stringify(queryObj)) : { query: {} };

        //strip out facets nested path for url serialization
        Object.keys(urlQuery.query).reduce((r, k) => {
            if (k.indexOf('facets.') > -1) {
                let [, alias] = k.split('.');
                r[alias] = r[k];
                delete r[k];
            }
            return r;
        }, urlQuery.query);

        //set product group key as page key
        urlQuery.pg = pg;

        //map category to alias for serialization
        if (urlQuery.query.cat) {
            let { cat } = urlQuery.query;
            urlQuery.query.cat = cat.map(_id => {
                if (customAggregations && customAggregations.category) {
                    let { category } = customAggregations;
                    return (category._id === _id && category.key) || _id;
                }
                return _id;
            });
        }

        let filterString = Helpers.getFilterString(urlQuery, true);
        return `${url}${filterString ? '/' : ''}${filterString}`;
    };

    getCustomContext = memoize(
        (pg = '', categoryFacets) => {
            let [, , tabName] = pg.split(':');
            return {
                categories: categoryFacets,
                metricsId: tabName ? `tab.${tabName}` : null,
            };
        },
        (pg, categoryFacets) => {
            return `${pg}-${categoryFacets && categoryFacets.length}`;
        }
    );

    mapSearchResults = hit => {
        return hit._id;
    };

    render() {
        const { slug, enableAppPDP, activePG, prefetchTabs, filtersObj, defaultPg, scrollTop, pg, isV2 } = this.props;
        //there is some renaming of the list of products, which I really hate
        //We can change it later, it happens in lots of places
        const { metaData = EMPTY_OBJECT, productsList = EMPTY_ARRAY, products = EMPTY_ARRAY } = activePG;
        const resolvedProductList = productsList.length === 0 ? products : productsList;
        const { categoryFacets = EMPTY_ARRAY, filterDisplayType, footerBanner = [] } = metaData;

        //if an display type is provided by an AB test use it
        const filterType = filterDisplayType || 'disabled';

        //disable search while waiting for async PG calls
        const disableSearch = categoryFacets.length === 0 || filterType === 'disabled';
        const searchKey = !disableSearch ? activePG.key : 'default';
        const isToggleFilter = (filterType === 'toggle' || filterType === 'toggle-expanded');
        
        return (
            <React.Fragment>
                <ConditionalWrapper
                    condition={filterType !== 'disabled' && activePG.key}
                    wrapper={children => {
                        return (
                            <SearchProvider
                                search={{
                                    query: this.tabQuery[searchKey] || {},
                                }}
                                searchFunc={this.fetchFilters}
                                searchKey={searchKey}
                                resultMapper={this.mapSearchResults}
                                urlSerialize={true}
                                urlFormatter={this.urlFormatter}
                                urlParser={urlParser}
                                disableSearch={disableSearch}
                                customContext={this.getCustomContext(activePG.key, categoryFacets)}
                                setFilterCount={setFilterCount}
                            >
                                {children}
                            </SearchProvider>
                        );
                    }}
                >
                    {/* /\ The searchKey ternary is a hacky solution to trick the getDerivedStateFromProps of Search Provider 
                                    to update only once the productGroup has been fully fetch*/}
                    <div
                        className={`sale-page clearfix${disableSearch ? '' : ' filters'}${isToggleFilter ? ' filter-toggle' : ''}`}
                    >
                        {isToggleFilter && <FilterCompareToggle defaultExpanded={filterType === 'toggle-expanded'} text={isV2 ? 'Filters' : undefined} />}
                        <LeftMenu
                            tabs={this.getTabs(disableSearch, pg, isToggleFilter, isV2)}
                            activePG={pg}
                            className={isV2 ? "v2" : undefined}
                            hideMobile={isV2}
                            minTabs={isV2 ? 2 : 1}
                            sticky={!isV2}
                        />
                        {isV2 ? <SaleProductList
                            pg={pg}
                            slug={slug}
                            scrollTop={!process.env.ISNODE && scrollTop ? scrollTop : null}
                            enableAppPDP={enableAppPDP}
                            prefetchTabs={prefetchTabs}
                        />: <ProductListContainer
                            pg={pg}
                            slug={slug}
                            scrollTop={!process.env.ISNODE && scrollTop ? scrollTop : null}
                            enableAppPDP={enableAppPDP}
                            prefetchTabs={prefetchTabs}
                        />}
                    </div>
                </ConditionalWrapper>
                {footerBanner.map((footer, i) => (
                    <FooterBanner key={`slp-footer-${i}`} footer={footer} />
                ))}
            </React.Fragment>
        );
    }
}

export default withError(SalePage);
